import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { ToolBar, DayWisePlanDataSequenceTable } from '..';
import { optionalColumnsWithShift } from '../../staticData.js';
import {
  filterRows,
  getDatesForRange,
  getoptionalColumnsByBU,
} from '../../utils/utils';
import styles from './Sequence.module.css';
import {
  API_RESOURCE_URLS,
  MESSAGE_TYPE,
  DISPLAY_MESSAGES,
  DATE_FORMAT,
  RESOURCE_TYPE,
  USER_OPERATIONS,
} from '../../constants';
import { API } from '../../apis/api';
import {
  getSequenceStartDateString,
  formatDataToIncludeColour,
} from '../../utils/tableUtils';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { withAllowedOperationsProvider } from '../../hocs';
import AuthEnabledButton from '../AuthEnabledButton/AuthEnabledButton';
import { buildErrorMessage } from '../../apis/calls';
import { useRef } from 'react';
import { connect } from 'react-redux';
import resequencingActions from '../../redux/features/resequencing/actions';
import { useHistory } from 'react-router-dom';

const Sequence = ({ initializeResequenceFlowForDateRange }) => {
  const { showPopup, showError, showLoading } = usePopupManager();
  const history = useHistory();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  const latestLineValueRef = useRef(line.value);
  const latestPlantValueRef = useRef(plant.value);

  const [planData, setPlanData] = useState({
    data: [],
    filteredData: [],
    startDate: '2020-05-26',
    endDate: '2020-06-20',
    lastUpdatedDate: null,
  });

  const [selectedOptionalCols, setSelectedOptionalCols] = useState([]);
  const [lastAsnGeneratedDate, setLastAsnGeneratedDate] = useState(null);
  const [isHeijunkaEnabled, setIsHeijunkaEnabled] = useState(false);
  const [isFetchingLastAgsnDate, setIsFetchingLastAgsnDate] = useState(false);
  const loadPlanFromDates = async (startDate, endDate) => {
    if (!plant.value || !line.value) return;
    const response = await API.get(API_RESOURCE_URLS.VC_QUANTITY, {
      params: {
        from: startDate,
        to: endDate,
        line: line.value,
        plant: plant.value,
      },
    });
    if (
      plant.value !== latestPlantValueRef.current ||
      line.value !== latestLineValueRef.current
    )
      return;
    const data = formatDataToIncludeColour(response.data);
    setPlanData({
      data,
      filteredData: data,
      startDate,
      endDate,
      lastUpdatedDate: moment(),
    });
  };

  const loadPlanDataOfNextThirtyDates = () => {
    const today = moment().format(DATE_FORMAT.ISO);
    const thiryDaysLaterDate = moment().add(30, 'days').format(DATE_FORMAT.ISO);
    return loadPlanFromDates(today, thiryDaysLaterDate);
  };

  const validateDates = (startDateString, endDateString) => {
    if (!startDateString) {
      return 'Please provide a start date';
    }
    if (!endDateString) {
      return 'Please provide an end date';
    }
    if (moment(endDateString).isBefore(moment(startDateString))) {
      return 'End date cannot be before start date';
    }
    return '';
  };

  const getQuantityWithTotalForSelectedDates = (
    quantity,
    startDate,
    endDate,
  ) => {
    const dateCopy = moment(startDate);
    const modifedQuantity = { ...quantity, totalForSelectedDates: 0 };
    while (dateCopy.isSameOrBefore(endDate, 'day')) {
      const dateString = dateCopy.format('YYYY-MM-DD');
      modifedQuantity.totalForSelectedDates +=
        modifedQuantity.plan[dateString]?.quantity || 0;
      dateCopy.add(1, 'days');
    }
    return modifedQuantity;
  };

  const getVcsWithNoCostReleaseForDates = async (
    allQuantities,
    startDateString,
    endDateString,
  ) => {
    const dateVcQuantitiesMap = {};

    const startDate = moment(startDateString, DATE_FORMAT.ISO);
    const endDate = moment(endDateString, DATE_FORMAT.ISO);

    allQuantities
      .map(quantity =>
        getQuantityWithTotalForSelectedDates(quantity, startDate, endDate),
      )
      .filter(quantity => quantity.totalForSelectedDates)
      .forEach(({ plan, vc }) => {
        Object.keys(plan).forEach(dateString => {
          if (dateVcQuantitiesMap[dateString])
            dateVcQuantitiesMap[dateString].push(vc);
          else dateVcQuantitiesMap[dateString] = [vc];
        });
      });

    const dates = getDatesForRange(startDate, endDate);
    const datesWithVcs = dates.filter(
      date => dateVcQuantitiesMap[date.format(DATE_FORMAT.ISO)]?.length > 0,
    );

    let vcsWithNoCostRelease;
    try {
      const responses = await Promise.all(
        datesWithVcs.map(date =>
          API.get(API_RESOURCE_URLS.VC_COST_RELEASE, {
            params: {
              plant: plant.value,
              plannedDate: date.format(DATE_FORMAT.ISO),
              material: dateVcQuantitiesMap[date.format(DATE_FORMAT.ISO)],
            },
          }),
        ),
      );

      vcsWithNoCostRelease = responses.reduce(
        (acc, { data }) => [...acc, ...data.notReleased],
        [],
      );
    } catch (error) {
      console.error(buildErrorMessage(error));
      showPopup({
        type: MESSAGE_TYPE.FAILURE,
        contextText: 'Failed to fetch VC cost release',
        error: error,
      });
    }

    return [...new Set(vcsWithNoCostRelease)];
  };

  const validateDataForSequenceGenerationForSelectedDates = async (
    startDateString,
    endDateString,
  ) => {
    const datesError = validateDates(startDateString, endDateString);
    if (datesError) {
      showPopup({ type: MESSAGE_TYPE.FAILURE, contextText: datesError });
      return false;
    }

    const closeLoadingPopup = showLoading();

    const vcsWithNoCostRelease = await getVcsWithNoCostReleaseForDates(
      planData.data,
      startDateString,
      endDateString,
    );

    if (!vcsWithNoCostRelease) {
      return false;
    } else if (vcsWithNoCostRelease.length) {
      showError({
        contextText: DISPLAY_MESSAGES.VC_COST_RELEASE_NOT_FOUND_FOR_SEQUENCE,
        info: vcsWithNoCostRelease,
      });
      return false;
    } else {
      closeLoadingPopup();
      return true;
    }
  };

  const applyHeijunka = async () => {
    const startDateString = getSequenceStartDateString(lastAsnGeneratedDate);
    const startDate = moment(startDateString, DATE_FORMAT.ISO);

    const success = await validateDataForSequenceGenerationForSelectedDates(
      startDateString,
      startDateString,
    );
    if (!success) {
      return;
    }

    const closeLoadingPopup = showLoading();
    try {
      await API.post(API_RESOURCE_URLS.VC_SEQUENCE, {
        from: startDateString,
        to: startDateString,
        line: line.value,
        plant: plant.value,
      });
      closeLoadingPopup();
      initializeResequenceFlowForDateRange(startDate, startDate);
      history.push('/sequence-previews');
    } catch (err) {
      console.error(buildErrorMessage(err));
      showPopup({
        type: MESSAGE_TYPE.FAILURE,
        error: err,
        contextText: DISPLAY_MESSAGES.ASN_GENERATION_ERROR,
      });

    }
  };

  const filterPlanData = filters => {
    const rows = filterRows(planData.data, filters);
    setPlanData(prevState => ({ ...prevState, filteredData: [...rows] }));
  };

  const loadLastAsnGeneratedDateForGivenPlantAndLine = async (
    selectedPlant,
    selectedLine,
  ) => {
    try {
      setIsFetchingLastAgsnDate(true)
      if (!plant.value || !line.value) return;
      const response = await API.get(
        API_RESOURCE_URLS.LAST_ASN_GENERATED_DATE,
        {
          params: {
            plant: selectedPlant,
            line: selectedLine,
          },
        },
      );
      if (
        selectedPlant !== latestPlantValueRef.current ||
        selectedLine !== latestLineValueRef.current
      )
        return;
      const data = response.data;
      const lineData = data.filter(entry => entry.line === selectedLine)[0];
      setLastAsnGeneratedDate(moment(lineData.lastDate, DATE_FORMAT.ISO));
      setIsHeijunkaEnabled(!!data.date)
    } catch (error) {
      console.warn(
        'failed to get last-asn-generated-date, err:' +
        buildErrorMessage(error),
      );
      setLastAsnGeneratedDate(null);
      setIsHeijunkaEnabled(false);
    } finally {
      setIsFetchingLastAgsnDate(false);
    }
  };

  useEffect(() => {
    latestLineValueRef.current = line.value;
    latestPlantValueRef.current = plant.value;

    loadPlanDataOfNextThirtyDates();
    loadLastAsnGeneratedDateForGivenPlantAndLine(plant.value, line.value);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, line.value]);

  return (
    <>
      <ToolBar
        optionalColumns={getoptionalColumnsByBU()}
        applyColumnsSelection={columnNames =>
          setSelectedOptionalCols([...columnNames])
        }
        selectedOptionalCols={selectedOptionalCols}
        lastUpdatedDate={planData.lastUpdatedDate}
        planData={planData}
        applyFilters={filters => {
          filterPlanData(filters);
        }}
        disableUpload
        disableMonthPicker
        disableFilter
        hasActiveEdits={false}
      />
      <div className={styles.container}>
        <div className={styles.tableView}>
          <div className={styles.table}>
            <DayWisePlanDataSequenceTable
              planData={planData}
              optionalColumns={optionalColumnsWithShift}
              selectedOptionalCols={selectedOptionalCols}
              lastAsnGeneratedDate={lastAsnGeneratedDate}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.datesContainer}>
            <div className={styles.dateLabel} htmlFor={'from-date'}>
              Sequence for
            </div>

            <div className={styles.dateValue} htmlFor={'from-date'}>
              {moment(
                getSequenceStartDateString(lastAsnGeneratedDate),
                DATE_FORMAT.ISO,
              ).format('DD-MM-YYYY')}
            </div>
          </div>
          <AuthEnabledButton
            operation={USER_OPERATIONS.CREATE_CHASSIS_PLAN_SEQUENCE}
            className={styles.footerButton}
            variant="contained"
            onClick={applyHeijunka}
            data-testid="gen-sequence-btn"
            disabled={!isHeijunkaEnabled && isFetchingLastAgsnDate}
          >
            Apply Heijunka
          </AuthEnabledButton>
        </div>
      </div>
    </>
  );
};

export default connect(null, {
  initializeResequenceFlowForDateRange:
    resequencingActions.initializeResequenceFlowForDateRange,
})(withAllowedOperationsProvider(Sequence, false, RESOURCE_TYPE.CHASSIS));
